// src/modules/purityAnalyzer/components/hud/XrfSpectrum.jsx
// XRF-style fluorescence spectrum rendered from the measured Au fraction.
// Peaks sit at each element's real characteristic X-ray energy (Au Lα, Cu Kα,
// Ag Kα, Zn Kα…) with heights scaled from the composition. The spectrum is a
// visualisation of the density measurement — not a real XRF acquisition —
// so the alloy peaks are indicative, matching the alloy display model.

import React, { useMemo } from 'react';
import { motion } from 'framer-motion';

// Characteristic emission lines (keV) and typical jeweller's alloy shares of
// the non-gold remainder. Shares are a display model only — see MetalComposition.
export const XRF_ELEMENTS = [
  { symbol: 'Au', name: 'Gold', color: '#ffd166', measured: true, line: 'Lα 9.71 keV', lines: [{ e: 9.713, rel: 1 }, { e: 11.442, rel: 0.52 }] },
  { symbol: 'Cu', name: 'Copper', color: '#ff9f6b', share: 0.55, line: 'Kα 8.05 keV', lines: [{ e: 8.041, rel: 1 }, { e: 8.905, rel: 0.17 }] },
  { symbol: 'Ag', name: 'Silver', color: '#c9d6e2', share: 0.30, line: 'Kα 22.16 keV', lines: [{ e: 22.163, rel: 1 }, { e: 2.984, rel: 0.3 }] },
  { symbol: 'Zn', name: 'Zinc', color: '#9be7ff', share: 0.15, line: 'Kα 8.64 keV', lines: [{ e: 8.631, rel: 1 }] },
];

// Split measured purity + remainder into per-element percentages.
export function buildComposition(purity = 0, otherMetalPercent) {
  const other = Number.isFinite(otherMetalPercent)
    ? otherMetalPercent
    : Math.max(0, 100 - purity);
  return XRF_ELEMENTS.map((el) => ({
    ...el,
    percent: el.measured ? purity : other * el.share,
  }));
}

const W = 720;
const H = 300;
const PAD = { l: 52, r: 16, t: 20, b: 36 };
const E_MAX = 24; // keV full scale
const PLOT_W = W - PAD.l - PAD.r;
const PLOT_H = H - PAD.t - PAD.b;

// Deterministic pseudo-noise so the trace looks acquired, not drawn.
const rnd = (i) => {
  const s = Math.sin(i * 127.1) * 43758.5453;
  return s - Math.floor(s);
};

export default function XrfSpectrum({ purity = 0, otherMetalPercent }) {
  const model = useMemo(() => {
    const composition = buildComposition(purity, otherMetalPercent);

    // Detector resolution broadens with energy.
    const sigma = (e) => 0.17 + e * 0.006;
    const peaks = composition.flatMap((el) =>
      el.lines.map((l) => ({
        e: l.e,
        amp: Math.pow(Math.max(el.percent, 0), 0.62) * l.rel,
        el,
        main: l.rel === 1,
      }))
    );

    const N = 420;
    const raw = [];
    for (let i = 0; i <= N; i++) {
      const e = (i / N) * E_MAX;
      // Bremsstrahlung-like continuum background
      let v = 0.9 * Math.exp(-((e - 6) ** 2) / 50) + 0.22;
      for (const p of peaks) {
        const s = sigma(p.e);
        v += p.amp * Math.exp(-((e - p.e) ** 2) / (2 * s * s));
      }
      raw.push(v);
    }
    const max = Math.max(...raw);
    const X = (e) => PAD.l + (e / E_MAX) * PLOT_W;
    const Y = (v) => PAD.t + PLOT_H - Math.max(0, Math.min(1, v)) * PLOT_H * 0.92;

    const pts = raw.map((v, i) => {
      const n = v / max;
      const noisy = n + (rnd(i) - 0.5) * (0.006 + n * 0.045);
      return [X((i / N) * E_MAX), Y(noisy)];
    });
    const trace = pts.map(([x, y], i) => `${i ? 'L' : 'M'}${x.toFixed(1)},${y.toFixed(1)}`).join(' ');
    const area = `${trace} L${X(E_MAX).toFixed(1)},${(PAD.t + PLOT_H).toFixed(1)} L${PAD.l},${(PAD.t + PLOT_H).toFixed(1)} Z`;

    // One label per element at its principal line.
    const labels = peaks
      .filter((p) => p.main)
      .map((p) => {
        const s = sigma(p.e);
        let v = (0.9 * Math.exp(-((p.e - 6) ** 2) / 50) + 0.22 + p.amp) / max;
        return {
          x: X(p.e),
          y: Math.max(PAD.t + 10, Y(v) - 10),
          peakY: Y(v),
          el: p.el,
        };
      });

    // Plausible full-scale count rate so the axis reads like an acquisition.
    const maxCps = Math.round((1200 + purity * 260) / 100) * 100;
    return { trace, area, labels, maxCps, composition };
  }, [purity, otherMetalPercent]);

  const yTicks = [0, 0.25, 0.5, 0.75, 1];
  const xTicks = [0, 4, 8, 12, 16, 20, 24];
  const axisText = { fill: 'rgba(148,197,222,0.6)', fontSize: 9, fontFamily: 'monospace' };

  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 'auto', display: 'block' }}>
      {/* Plot frame + grid */}
      <rect x={PAD.l} y={PAD.t} width={PLOT_W} height={PLOT_H}
        fill="rgba(0,229,255,0.02)" stroke="rgba(0,229,255,0.18)" />
      {yTicks.map((t) => (
        <g key={`y${t}`}>
          <line x1={PAD.l} x2={PAD.l + PLOT_W}
            y1={PAD.t + PLOT_H - t * PLOT_H} y2={PAD.t + PLOT_H - t * PLOT_H}
            stroke="rgba(0,229,255,0.07)" />
          <text x={PAD.l - 6} y={PAD.t + PLOT_H - t * PLOT_H + 3} textAnchor="end" {...axisText}>
            {t === 0 ? '0' : `${((model.maxCps * t) / 1000).toFixed(1)}k`}
          </text>
        </g>
      ))}
      {xTicks.map((t) => (
        <g key={`x${t}`}>
          <line x1={PAD.l + (t / E_MAX) * PLOT_W} x2={PAD.l + (t / E_MAX) * PLOT_W}
            y1={PAD.t} y2={PAD.t + PLOT_H} stroke="rgba(0,229,255,0.07)" />
          <text x={PAD.l + (t / E_MAX) * PLOT_W} y={H - PAD.b + 14} textAnchor="middle" {...axisText}>
            {t}
          </text>
        </g>
      ))}

      {/* Axis titles */}
      <text x={PAD.l + PLOT_W / 2} y={H - 6} textAnchor="middle" {...axisText} letterSpacing="0.2em">
        ENERGY · keV
      </text>
      <text x={12} y={PAD.t + PLOT_H / 2} textAnchor="middle" {...axisText} letterSpacing="0.2em"
        transform={`rotate(-90 12 ${PAD.t + PLOT_H / 2})`}>
        INTENSITY · CPS
      </text>

      {/* Spectrum area + trace */}
      <defs>
        <linearGradient id="pa-xrf-fill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="rgba(0,229,255,0.30)" />
          <stop offset="100%" stopColor="rgba(0,229,255,0.02)" />
        </linearGradient>
      </defs>
      <motion.path d={model.area} fill="url(#pa-xrf-fill)" stroke="none"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.9, delay: 0.5 }} />
      <motion.path d={model.trace} fill="none" stroke="#7fefff" strokeWidth="1.4"
        style={{ filter: 'drop-shadow(0 0 3px rgba(0,229,255,0.55))' }}
        initial={{ pathLength: 0 }} animate={{ pathLength: 1 }}
        transition={{ duration: 1.4, ease: 'easeInOut' }} />

      {/* Peak markers + element labels */}
      {model.labels.map(({ x, y, peakY, el }, i) => (
        <motion.g key={el.symbol}
          initial={{ opacity: 0 }} animate={{ opacity: 1 }}
          transition={{ delay: 1.1 + i * 0.15 }}>
          <line x1={x} x2={x} y1={peakY} y2={PAD.t + PLOT_H}
            stroke={el.color} strokeOpacity="0.35" strokeDasharray="2 3" />
          <text x={x} y={y - 10} textAnchor="middle" fill={el.color}
            fontSize="12" fontWeight="700" fontFamily="monospace">
            {el.symbol}
          </text>
          <text x={x} y={y + 1} textAnchor="middle" fill={el.color} fillOpacity="0.75"
            fontSize="8.5" fontFamily="monospace">
            {el.percent.toFixed(el.measured ? 2 : 1)}%
          </text>
        </motion.g>
      ))}
    </svg>
  );
}
